import type { ApiSchema } from "../apiSchema";
import { jsonBody, requestJson, withQuery } from "../http/httpClient";
import { readObject, readString } from "../http/jsonDecoding";
import { readThreadEntry, readThreadSettings, type AgentRoute, type ApiKeyRoute, type ThreadEntry, type ThreadSettings } from "./liveMessages";
import { decodeRoute } from "./routeOptions";
import { completeSettings } from "./threadsApi";
import {
  decodeHostOffer,
  decodeHostOffers,
  decodeIncomingHostOffers,
  decodeThreadOverview,
  type HostOffer,
  type IncomingHostOffer,
  type ThreadOverview,
} from "./threadDecoders";

/**
 * Hosting a Thread (threads.md T-8, T-9): attach and detach the host's connector, close and
 * reopen, and hand-off by passing the host role or offering a fork. Module C checks who may;
 * routes: `server/src/dmw/api/routes/threads/hosting.py`.
 */

function threadPath(threadId: string, rest: string): `/${string}` {
  return `/threads/${encodeURIComponent(threadId)}${rest}`;
}


/**
 * Where the Thread would run for the signed-in user without asking (F55), as the server alone
 * decides it (BA12-02): their online connector, its route, and the Thread's settings with that
 * choice (`null` when it changes none). `null` when they choose.
 */
export interface AutomaticHost {
  readonly connectorId: string;
  /** `null`: no agent step is left to run, so the Thread goes live with no route. */
  readonly route: AgentRoute | ApiKeyRoute | null;
  readonly settings: ThreadSettings | null;
}

/** `connectorId`: run on that connector of yours, as the start chose it (F106); else the server picks. */
export function readAutomaticHost(threadId: string, signal?: AbortSignal, connectorId: string | null = null): Promise<AutomaticHost | null> {
  return requestJson({
    method: "GET",
    path: connectorId === null ? threadPath(threadId, "/automatic-host") : withQuery(threadPath(threadId, "/automatic-host"), { connector_id: connectorId }),
    signal,
    decodeResponse: decodeAutomaticHost,
  });
}

/**
 * The same before a Thread exists (F106, TR-A2-04): where a start of the Workflow would run for
 * you without asking, so the start form begins from hosting's own choice. `null` when you choose.
 */
export function readStartAutomaticHost(question: { readonly projectId: string; readonly workflow: string; readonly contentVersion: string }, signal?: AbortSignal): Promise<AutomaticHost | null> {
  const body = { workflow: question.workflow, content_version: question.contentVersion } satisfies ApiSchema<"StartQuestionBody">;
  return requestJson({ method: "POST", path: `/projects/${encodeURIComponent(question.projectId)}/automatic-host`, body: jsonBody(body), signal, decodeResponse: decodeAutomaticHost });
}

function decodeAutomaticHost(body: unknown): AutomaticHost | null {
  if (body === null) return null;
  const host = readObject(body, "automatic host");
  return {
    connectorId: readString(host, "connector_id"),
    route: host["route"] === null ? null : decodeRoute(readObject(host["route"], "route")),
    settings: host["settings"] === null ? null : readThreadSettings(host["settings"]),
  };
}

/** Make the Thread live on one of the host's online connectors. */
/**
 * Live on (or moved to) `connectorId`; with `chosen`, the settings the host chose are written
 * first, conditional on the entry they saw, in the same step (TR-B1b-02).
 */
export function attachConnector(threadId: string, connectorId: string, chosen: { readonly settings: ThreadSettings; readonly lastEntryNumber: number } | null = null): Promise<ThreadOverview> {
  const body = { connector_id: connectorId, ...(chosen === null ? {} : { settings: completeSettings(chosen.settings), expected_last_entry_number: chosen.lastEntryNumber }) } satisfies ApiSchema<"AttachBody">;
  return requestJson({ method: "POST", path: threadPath(threadId, "/attach"), body: jsonBody(body), decodeResponse: decodeThreadOverview });
}

/** A Thread closed before F69 opens again, saved; Threads are put away by settling them now. */
export function reopenThread(threadId: string): Promise<ThreadOverview> {
  return requestJson({ method: "POST", path: threadPath(threadId, "/reopen"), decodeResponse: decodeThreadOverview });
}

export function offerHostRole(threadId: string, toUser: string): Promise<HostOffer> {
  const body = { to_user: toUser } satisfies ApiSchema<"HostOfferRequestBody">;
  return requestJson({ method: "POST", path: threadPath(threadId, "/host-offers"), body: jsonBody(body), decodeResponse: decodeHostOffer });
}

/** What waits for the signed-in user to decide: offers made to them, requests for their role; oldest first. */
export function listIncomingHostOffers(signal?: AbortSignal): Promise<readonly IncomingHostOffer[]> {
  return requestJson({ method: "GET", path: "/host-offers", signal, decodeResponse: decodeIncomingHostOffers });
}

/** The caller's own open offers and requests in a Thread, to withdraw one after a reload. */
export function listThreadHostOffers(threadId: string, signal?: AbortSignal): Promise<readonly HostOffer[]> {
  return requestJson({ method: "GET", path: threadPath(threadId, "/host-offers"), signal, decodeResponse: decodeHostOffers });
}

/** Take the host role with one of the user's online connectors; conditional on the log. */
export function acceptHostOffer(offerId: string, connectorId: string, expectedLastEntryNumber: number): Promise<ThreadOverview> {
  const body = {
    connector_id: connectorId,
    expected_last_entry_number: expectedLastEntryNumber,
  } satisfies ApiSchema<"AcceptHostOfferBody">;
  return requestJson({
    method: "POST",
    path: `/host-offers/${encodeURIComponent(offerId)}/accept`,
    body: jsonBody(body),
    decodeResponse: decodeThreadOverview,
  });
}

/**
 * Ask the host for their role (F55, Take over), to run the Thread on one of the user's online
 * connectors with `settings`, which the server sets as the new host's once approved.
 */
export function requestHostRole(threadId: string, connectorId: string, settings: ThreadSettings): Promise<HostOffer> {
  const body = { connector_id: connectorId, settings: completeSettings(settings) } satisfies ApiSchema<"HostRequestBody">;
  return requestJson({ method: "POST", path: threadPath(threadId, "/host-requests"), body: jsonBody(body), decodeResponse: decodeHostOffer });
}

/** The host approves a request for their role: the asker takes it over; conditional on the log. */
export function approveHostRequest(offerId: string, expectedLastEntryNumber: number): Promise<ThreadOverview> {
  const body = { expected_last_entry_number: expectedLastEntryNumber } satisfies ApiSchema<"ApproveHostRequestBody">;
  return requestJson({ method: "POST", path: `/host-offers/${encodeURIComponent(offerId)}/approve`, body: jsonBody(body), decodeResponse: decodeThreadOverview });
}

/** Decline an offer made to the user, or a request for their role. */
export function declineHostOffer(offerId: string): Promise<HostOffer> {
  return requestJson({ method: "POST", path: `/host-offers/${encodeURIComponent(offerId)}/decline`, decodeResponse: decodeHostOffer });
}

/** Withdraw the user's own offer or request. */
export function withdrawHostOffer(offerId: string): Promise<HostOffer> {
  return requestJson({ method: "POST", path: `/host-offers/${encodeURIComponent(offerId)}/withdraw`, decodeResponse: decodeHostOffer });
}

/** Offer a participant to fork at an entry; appends `fork_offered`. */
export function offerFork(threadId: string, toUser: string, atEntry: number): Promise<ThreadEntry> {
  const body = { to_user: toUser, at_entry: atEntry } satisfies ApiSchema<"ForkOfferRequestBody">;
  return requestJson({ method: "POST", path: threadPath(threadId, "/fork-offers"), body: jsonBody(body), decodeResponse: readThreadEntry });
}

/** Fork at the offered entry in one action; the new Thread names the offer. */
export function acceptForkOffer(threadId: string, offerEntry: number, title: string, clientRequestId: string): Promise<ThreadOverview> {
  const body = { title, client_request_id: clientRequestId } satisfies ApiSchema<"AcceptForkOfferBody">;
  return requestJson({
    method: "POST",
    path: threadPath(threadId, `/fork-offers/${String(offerEntry)}/accept`),
    body: jsonBody(body),
    decodeResponse: decodeThreadOverview,
  });
}
